// frontend/src/components/employee/AddLandlordModal.js

import { useState, useEffect } from 'react';
import api from '../../utils/api';

const lbl = { fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 };
const inp = { width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#1e293b', outline: 'none', background: 'white', boxSizing: 'border-box' };

const EMPTY = { first_name: '', last_name: '', company_name: '', email: '', phone: '', address: '', account_manager_id: '', send_invite: true };

export default function AddLandlordModal({ onClose, onSuccess }) {
  const [form, setForm] = useState(EMPTY);
  const [managers, setManagers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);

  useEffect(() => {
    api.get('/employee/staff').then(res => {
      setManagers((res.data.staff || []).filter(s => s.role === 'account_manager' && s.status === 'active'));
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    if (!form.first_name || !form.last_name || !form.email) {
      setError('First name, last name, and email are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await api.post('/employee/landlords', form);
      setCreated(res.data);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not create landlord');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 16px', borderBottom: '1px solid #e5e7eb' }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Add New Landlord</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#6b7280' }}>✕</button>
        </div>

        <div style={{ padding: '20px 24px 24px' }}>
          {!created ? (
            <>
              {error && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 13, marginBottom: 16 }}>{error}</div>}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={lbl}>First Name *</label>
                  <input value={form.first_name} onChange={e => setForm(p => ({ ...p, first_name: e.target.value }))} style={inp} placeholder="John" />
                </div>
                <div>
                  <label style={lbl}>Last Name *</label>
                  <input value={form.last_name} onChange={e => setForm(p => ({ ...p, last_name: e.target.value }))} style={inp} placeholder="Smith" />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={lbl}>Company / Property Group Name</label>
                  <input value={form.company_name} onChange={e => setForm(p => ({ ...p, company_name: e.target.value }))} style={inp} placeholder="Smith Properties LLC" />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={lbl}>Email Address *</label>
                  <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} style={inp} placeholder="john@smithproperties.com" />
                </div>
                <div>
                  <label style={lbl}>Phone</label>
                  <input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} style={inp} placeholder="212-555-1234" />
                </div>
                <div>
                  <label style={lbl}>Account Manager</label>
                  <select value={form.account_manager_id} onChange={e => setForm(p => ({ ...p, account_manager_id: e.target.value }))} style={inp}>
                    <option value="">Unassigned</option>
                    {managers.map(m => <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>)}
                  </select>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={lbl}>Address</label>
                  <input value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} style={inp} placeholder="123 Main St, New York, NY 10001" />
                </div>
                <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 10, background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '12px 14px' }}>
                  <input type="checkbox" id="send_invite" checked={form.send_invite} onChange={e => setForm(p => ({ ...p, send_invite: e.target.checked }))} style={{ width: 16, height: 16 }} />
                  <label htmlFor="send_invite" style={{ fontSize: 13, color: '#0369a1', fontWeight: 500 }}>
                    Show temporary password after creation so I can share login credentials
                  </label>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <button onClick={onClose} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '10px 18px', fontSize: 14, cursor: 'pointer', fontWeight: 500 }}>Cancel</button>
                <button onClick={handleSave} disabled={saving} style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
                  {saving ? 'Creating...' : 'Create Landlord'}
                </button>
              </div>
            </>
          ) : (
            /* Success screen */
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
              <h3 style={{ margin: '0 0 8px', fontSize: 20 }}>Landlord Created!</h3>
              <p style={{ color: '#64748b', fontSize: 14, marginBottom: 20 }}>
                {created.landlord.first_name} {created.landlord.last_name} has been added successfully.
              </p>

              {created.temp_password && (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '16px 20px', marginBottom: 20, textAlign: 'left' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#16a34a', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Temporary Login Credentials</div>
                  <div style={{ fontSize: 13, marginBottom: 4 }}><strong>Email:</strong> {created.landlord.email}</div>
                  <div style={{ fontSize: 13, marginBottom: 12 }}><strong>Temp Password:</strong> <code style={{ background: '#dcfce7', padding: '2px 8px', borderRadius: 4, fontWeight: 700 }}>{created.temp_password}</code></div>
                  <div style={{ fontSize: 11, color: '#16a34a' }}>Share these credentials with the landlord. They can change their password after logging in.</div>
                </div>
              )}

              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 20px', textAlign: 'left', marginBottom: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Landlord Portal Login</div>
                <div style={{ fontSize: 13, color: '#2563eb' }}>https://bulknet-frontend.vercel.app/landlord/login</div>
              </div>

              <button onClick={onClose} style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
